import React from 'react';

// Small SVG charts for the Insights view. No chart library: the app has none, and these few shapes don't need one.
// Following SAP Fiori's analytical card rules: no more than two series per column chart, no donuts, and every bar carries
// its number as text.

// A round axis maximum and its ticks: 4 steps of 1, 2, 2.5 or 5 × 10^n.
export function niceTicks(max, steps = 4) {
	if (!(max > 0)) return [0, 1, 2, 3, 4].slice(0, steps + 1);
	const raw = max / steps;
	const p = Math.pow(10, Math.floor(Math.log10(raw)));
	const step = [1, 2, 2.5, 5, 10].map(f => f * p).find(s => s >= raw);
	return Array.from({ length: steps + 1 }, (_, i) => Math.round(i * step * 1000) / 1000);
}

export function ColumnChart({ labels, series, unit, height = 210, width = 720, label }) {
	const W = width, H = height, x0 = 36, x1 = width - 8, y0 = 12, y1 = H - 36;
	const max = Math.max(1, ...series.flatMap(s => s.values));
	const ticks = niceTicks(max);
	const top = ticks[ticks.length - 1];
	const y = v => y1 - (v / top) * (y1 - y0);
	const gw = (x1 - x0) / Math.max(1, labels.length);
	const bw = Math.min(series.length > 1 ? 22 : 40, (gw - 10) / series.length);
	const every = Math.ceil(labels.length / 12); // at most 12 labels on the axis
	return (
		<svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={label} className="apw2-chart">
			{ticks.map(t => (
				<g key={t}><line x1={x0} x2={x1} y1={y(t)} y2={y(t)} className="grid" /><text x={x0 - 8} y={y(t) + 3.5} textAnchor="end">{t}</text></g>
			))}
			{labels.map((l, i) => {
				const cx = x0 + gw * i + gw / 2;
				return (
					<g key={i}>
						{series.map((s, j) => {
							const v = s.values[i] || 0;
							const x = cx - (bw * series.length + 3 * (series.length - 1)) / 2 + j * (bw + 3);
							return <rect key={s.key} x={x} y={y(v)} width={bw} height={Math.max(0, y1 - y(v))} rx="2" style={{ fill: s.color }}><title>{`${s.label}, ${l}: ${v}`}</title></rect>;
						})}
						{i % every === 0 && <text x={cx} y={y1 + 16} textAnchor="middle">{l}</text>}
					</g>
				);
			})}
			{unit && <text x={x1} y={H - 2} textAnchor="end">{unit}</text>}
		</svg>
	);
}

export function Legend({ items }) {
	return <div className="apw2-legend">{items.map(i => <span key={i.label}><i style={{ background: i.color }} />{i.label}</span>)}</div>;
}

// Horizontal bars. rows: [{ key, label, value, color, text, onClick, title }]
export function BarList({ rows, max, label }) {
	const top = Math.max(1, max != null ? max : Math.max(0, ...rows.map(r => r.value)));
	return (
		<div className="apw2-barlist" role="list" aria-label={label}>
			{rows.map(r => {
				const inner = (
					<>
						<span className="bl">{r.label}</span>
						<span className="bt" aria-hidden="true"><i style={{ width: `${Math.min(100, (r.value / top) * 100)}%`, background: r.color }} /></span>
						<span className="bn">{r.text != null ? r.text : r.value}</span>
					</>
				);
				return r.onClick
					? <button type="button" role="listitem" key={r.key} className="apw2-hbar" onClick={r.onClick} title={r.title}>{inner}</button>
					: <div role="listitem" key={r.key} className="apw2-hbar" title={r.title}>{inner}</div>;
			})}
		</div>
	);
}

export function Sparkline({ values, color, label }) {
	if (!values || values.length < 2) return null;
	const w = 150, h = 26;
	const mx = Math.max(...values), mn = Math.min(...values);
	const pts = values.map((v, i) => `${((i / (values.length - 1)) * w).toFixed(1)},${(h - 2 - ((v - mn) / (mx - mn || 1)) * (h - 4)).toFixed(1)}`).join(' ');
	return (
		<svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none" role="img" aria-label={label} className="apw2-spark">
			<polyline points={pts} fill="none" style={{ stroke: color }} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
		</svg>
	);
}

// One bar split into parts. parts: [{ key, label, value, color }]
export function StackBar({ parts, label }) {
	const total = parts.reduce((a, p) => a + p.value, 0) || 1;
	return (
		<div className="apw2-stack" role="img" aria-label={label}>
			{parts.filter(p => p.value > 0).map(p => <i key={p.key} style={{ width: `${(p.value / total) * 100}%`, background: p.color }} title={`${p.label}: ${p.value}`} />)}
		</div>
	);
}
